import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, BarChart3, Calendar, PieChart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { getMonthlyTracking } from '../../shared/utils/comptabiliteService';
import { formatComptabiliteMoney, formatComptabiliteMoneyCompact } from './utils/comptabiliteSaudiFormat';
import { formatChartMonthKey } from '../../shared/utils/chartFormatters';
import { useSaudiDateFilter } from '../../products/saudi/context/SaudiDateFilterContext';
import { isMonthKeyInRange } from '../../products/saudi/utils/dateRangePresets';

const EMPTY_TRACKING = {
  overview: { avgGrowth: 0, forecast2025: 0, completedMonths: 0, lastUpdate: '—' },
  data: { '1an': { months: [], kpis: {} } },
};

function rowMargin(row) {
  if (row.marge != null) return Number(row.marge);
  const ca = Number(row.chiffreAffaires || 0);
  const res = Number(row.resultat || 0);
  if (!ca) return 0;
  return Math.round((res / ca) * 1000) / 10;
}

function computeGrowth(monthRows) {
  const growthValues = monthRows.slice(1).map((row, idx) => {
    const prev = monthRows[idx]?.chiffreAffaires || 0;
    if (!prev) return 0;
    return ((row.chiffreAffaires - prev) / prev) * 100;
  });
  if (!growthValues.length) return 0;
  return Math.round((growthValues.reduce((a, b) => a + b, 0) / growthValues.length) * 10) / 10;
}

function computeKpis(monthRows) {
  const totalCA = monthRows.reduce((sum, row) => sum + Number(row.chiffreAffaires || 0), 0);
  const totalCharges = monthRows.reduce((sum, row) => sum + Number(row.charges || 0), 0);
  const totalResultat = monthRows.reduce((sum, row) => sum + Number(row.resultat || 0), 0);
  return {
    totalCA,
    totalCharges,
    totalResultat,
    croissanceCA: computeGrowth(monthRows),
  };
}

export default function AnalysesRapportsPage() {
  const { t } = useTranslation();
  const p = useCallback((key, opts) => t(`modules.comptabilite.pages.analysesRapportsPage.${key}`, opts), [t]);
  const dash = useCallback((key, opts) => t(`modules.comptabilite.pages.accountingDashboardPage.${key}`, opts), [t]);
  const { range } = useSaudiDateFilter();

  const [tracking, setTracking] = useState(EMPTY_TRACKING);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadData = async () => {
    setLoading(true);
    try {
      const monthly = await getMonthlyTracking();
      setTracking({ ...EMPTY_TRACKING, ...(monthly || {}) });
    } catch (error) {
      console.error('Comptabilite analytics load error:', error);
      setTracking(EMPTY_TRACKING);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const overview = tracking.overview || EMPTY_TRACKING.overview;
  const yearData = tracking.data?.['1an'] || { months: [], kpis: {} };
  const allMonths = yearData.months || [];

  const filteredMonths = useMemo(
    () => allMonths.filter((row) => isMonthKeyInRange(row.month, range)),
    [allMonths, range],
  );

  const kpis = useMemo(() => computeKpis(filteredMonths), [filteredMonths]);

  const insights = useMemo(() => {
    if (!filteredMonths.length) {
      return [
        { label: p('insight_avg_margin'), value: loading ? '…' : '—' },
        { label: p('insight_growth_trend'), value: loading ? '…' : '—' },
        { label: p('insight_best_month'), value: loading ? '…' : '—' },
      ];
    }

    const margins = filteredMonths.map(rowMargin);
    const avgMargin = Math.round((margins.reduce((a, b) => a + b, 0) / margins.length) * 10) / 10;
    const growth = kpis.croissanceCA ?? overview.avgGrowth ?? 0;

    const bestRow = filteredMonths.reduce(
      (best, row) => (Number(row.chiffreAffaires || 0) > Number(best.chiffreAffaires || 0) ? row : best),
      filteredMonths[0],
    );
    const bestLabel = formatChartMonthKey(bestRow.month, { fallback: '—' }) || '—';

    return [
      {
        label: p('insight_avg_margin'),
        value: `${avgMargin}%`,
        hint: p('insight_avg_margin_hint', { count: filteredMonths.length }),
      },
      {
        label: p('insight_growth_trend'),
        value: `${growth}%`,
        hint: growth >= 0 ? p('insight_growth_up') : p('insight_growth_down'),
      },
      {
        label: p('insight_best_month'),
        value: formatComptabiliteMoneyCompact(bestRow.chiffreAffaires || 0),
        hint: bestLabel,
      },
    ];
  }, [filteredMonths, kpis, overview, loading, p]);

  const stats = useMemo(
    () => [
      {
        label: dash('kpi_revenue'),
        value: loading ? '…' : formatComptabiliteMoneyCompact(kpis.totalCA || 0),
        icon: TrendingUp,
        variant: 'forest',
      },
      {
        label: dash('kpi_net_result'),
        value: loading ? '…' : formatComptabiliteMoneyCompact(kpis.totalResultat || 0),
        icon: PieChart,
        variant: 'copper',
      },
      {
        label: dash('kpi_expenses'),
        value: loading ? '…' : formatComptabiliteMoneyCompact(kpis.totalCharges || 0),
        icon: BarChart3,
        variant: 'forest',
      },
      {
        label: t('common.growth', { defaultValue: 'النمو' }),
        value: loading ? '…' : `${kpis.croissanceCA ?? overview.avgGrowth ?? 0}%`,
        icon: Calendar,
        variant: 'copper',
      },
    ],
    [loading, kpis, overview, dash, t],
  );

  const charts = useMemo(() => {
    const chartMonths = filteredMonths.slice(-6);
    const monthLabel = (row) => formatChartMonthKey(row.month, { fallback: '—' }) || '—';

    const revenueRows = chartMonths.map((row) => ({
      label: monthLabel(row),
      value: Number(row.chiffreAffaires || 0),
      display: formatComptabiliteMoney(row.chiffreAffaires || 0),
    }));

    const chargesRows = chartMonths.map((row) => ({
      label: monthLabel(row),
      value: Number(row.charges || 0),
      display: formatComptabiliteMoney(row.charges || 0),
    }));

    const positiveMonths = filteredMonths.filter((row) => Number(row.resultat || 0) >= 0).length;
    const negativeMonths = Math.max(0, filteredMonths.length - positiveMonths);

    return [
      {
        title: p('chart_revenue_trend'),
        subtitle: p('chart_revenue_subtitle'),
        variant: 'pipeline',
        rows: revenueRows,
      },
      {
        title: p('chart_charges_trend'),
        subtitle: p('chart_charges_subtitle'),
        variant: 'ranked',
        rows: chargesRows,
      },
      {
        title: p('chart_result_status'),
        subtitle: p('chart_result_subtitle'),
        variant: 'status',
        active: positiveMonths,
        inactive: negativeMonths,
      },
    ];
  }, [filteredMonths, p]);

  const table = useMemo(() => {
    const rows = filteredMonths.map((row) => ({
      period: formatChartMonthKey(row.month, { fallback: '—' }) || '—',
      revenue: formatComptabiliteMoney(row.chiffreAffaires || 0),
      charges: formatComptabiliteMoney(row.charges || 0),
      result: formatComptabiliteMoney(row.resultat || 0),
      margin: `${rowMargin(row)}%`,
    }));

    return {
      title: t('common.summary', { defaultValue: 'ملخص' }),
      columns: [
        { key: 'period', label: t('common.period', { defaultValue: 'الفترة' }) },
        { key: 'revenue', label: dash('kpi_revenue') },
        { key: 'charges', label: dash('kpi_expenses') },
        { key: 'result', label: dash('kpi_net_result') },
        { key: 'margin', label: t('common.margin', { defaultValue: 'الهامش %' }) },
      ],
      rows,
    };
  }, [filteredMonths, dash, t]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={() => setRefreshTrigger((n) => n + 1)}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="comptabilite_analyses"
      emptyLabel={t('common.no_data')}
    />
  );
}
